import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TaskFilter, TaskTag, TaskStatus, UserDetail } from '../../../models/task-management.models';
import { TimezoneService } from '../../../services/timezone.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-task-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    DatePickerModule,
    ToggleSwitchModule
  ],
  template: `
    <div class="filter-panel">

      <!-- Header -->
      <div class="filter-header">
        <span class="filter-title">Filters</span>
        <button class="filter-close-btn" type="button" (click)="onClose()">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="filter-body">

        <!-- Search -->
        <div class="filter-field">
          <label class="filter-label">Search</label>
          <div class="search-input-wrap">
            <input
              type="text"
              class="filter-input"
              placeholder="Search tasks..."
              [(ngModel)]="localSearch"
              (input)="onSearchInput()" />
            <i class="ri-search-line search-icon"></i>
          </div>
        </div>

        <!-- Status -->
        <div class="filter-field" *ngIf="availableStatuses?.length">
          <label class="filter-label">Status</label>
          <p-select
            [options]="availableStatuses"
            [(ngModel)]="localStatusId"
            optionLabel="statusName"
            optionValue="statusId"
            placeholder="Select status"
            [showClear]="true"
            styleClass="filter-select" />
        </div>

        <!-- Assignee -->
        <div class="filter-field" *ngIf="availableUsers?.length">
          <label class="filter-label">Assignee</label>
          <p-select
            [options]="availableUsers"
            [(ngModel)]="localAssigneeId"
            optionLabel="name"
            optionValue="userId"
            placeholder="Select assignee"
            [showClear]="true"
            styleClass="filter-select" />
        </div>

        <!-- Tags -->
        <div class="filter-field" *ngIf="availableTags?.length">
          <label class="filter-label">Tags</label>
          <p-select
            [options]="availableTags"
            [(ngModel)]="localTagId"
            optionLabel="tagName"
            optionValue="tagId"
            placeholder="Select tag"
            [showClear]="true"
            styleClass="filter-select" />
        </div>

        <!-- Start Date -->
        <div class="filter-field">
          <label class="filter-label">Start Date</label>
          <p-datepicker
            [(ngModel)]="localDateFrom"
            placeholder="Select start date"
            [showIcon]="true"
            [showClear]="true"
            styleClass="filter-datepicker" />
        </div>

        <!-- End Date -->
        <div class="filter-field">
          <label class="filter-label">End Date</label>
          <p-datepicker
            [(ngModel)]="localDateTo"
            placeholder="Select end date"
            [showIcon]="true"
            [showClear]="true"
            styleClass="filter-datepicker" />
        </div>

        <!-- Show Inactive -->
        <div class="filter-field filter-toggle-field">
          <span class="filter-label">Show inactive task only</span>
          <p-toggleswitch [(ngModel)]="localShowInactive" />
        </div>

      </div>

      <!-- Footer -->
      <div class="filter-footer">
        <button class="btn-clear-all" type="button" (click)="clearAll()">Clear All</button>
        <div class="footer-actions">
          <button class="btn-close" type="button" (click)="onClose()">Close</button>
          <button class="btn-apply" type="button" (click)="applyFilters()">Apply</button>
        </div>
      </div>

    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .filter-panel {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #ffffff;
    }

    /* Header */
    .filter-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 16px 12px 16px;
      border-bottom: 1px solid #E7E7E9;
      flex-shrink: 0;
    }

    .filter-title {
      font-family: 'Inter', sans-serif;
      font-size: 18px;
      font-weight: 600;
      color: #091F2D;
      line-height: 1.2;
    }

    .filter-close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #606576;
      transition: background 0.15s, color 0.15s;
      padding: 0;
    }

    .filter-close-btn:hover {
      background: #f4f4f6;
      color: #091F2D;
    }

    .filter-close-btn i {
      font-size: 20px;
    }

    /* Body */
    .filter-body {
      flex: 1;
      overflow-y: auto;
      padding: 10px 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-height: 0;
    }

    .filter-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .filter-label {
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #091F2D;
      line-height: 1.4;
    }

    /* Search */
    .search-input-wrap {
      position: relative;
    }

    .filter-input {
      width: 100%;
      height: 38px;
      border: 1px solid #CDCFD6;
      border-radius: 6px;
      padding: 0 36px 0 12px;
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      color: #091F2D;
      background: #ffffff;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.15s;
    }

    .filter-input:focus {
      border-color: #0C3257;
    }

    .filter-input::placeholder {
      color: #787E91;
    }

    .search-icon {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 18px;
      color: #787E91;
      pointer-events: none;
    }

    /* Select */
    :host ::ng-deep .filter-select {
      width: 100%;
    }

    :host ::ng-deep .filter-select .p-select {
      width: 100%;
      height: 38px;
      border: 1px solid #CDCFD6;
      border-radius: 6px;
      background: #ffffff;
    }

    :host ::ng-deep .filter-select .p-select:not(.p-disabled):hover {
      border-color: #0C3257;
    }

    :host ::ng-deep .filter-select .p-select-label {
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      color: #091F2D;
      padding: 0 12px;
      line-height: 38px;
    }

    :host ::ng-deep .filter-select .p-select-label.p-placeholder {
      color: #787E91;
    }

    :host ::ng-deep .filter-datepicker {
      width: 100%;
    }

    :host ::ng-deep .filter-datepicker .p-datepicker-input-icon-container {
      width: 100%;
    }

    :host ::ng-deep .filter-datepicker .p-inputtext {
      width: 100%;
      height: 38px;
      border: 1px solid #CDCFD6;
      border-radius: 6px;
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      color: #091F2D;
      padding: 0 36px 0 10px;
    }

    :host ::ng-deep .filter-datepicker .p-inputtext::placeholder {
      color: #787E91;
    }

    /* Toggle */
    .filter-toggle-field {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .filter-toggle-field .filter-label {
      flex: 1;
    }

    /* Footer */
    .filter-footer {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 10px 16px 12px 16px;
      border-top: 1px solid #E7E7E9;
      flex-shrink: 0;
    }

    .btn-clear-all {
      height: 28px;
      border: none;
      background: transparent;
      border-radius: 6px;
      font-family: 'Lato', sans-serif;
      font-size: 13px;
      font-weight: 500;
      color: #787E91;
      cursor: pointer;
      text-decoration: underline;
      transition: color 0.15s;
    }

    .btn-clear-all:hover {
      color: #091F2D;
    }

    .footer-actions {
      display: flex;
      gap: 10px;
    }

    .btn-close {
      flex: 1;
      height: 40px;
      border: 1px solid #CDCFD6;
      background: transparent;
      border-radius: 6px;
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #606576;
      cursor: pointer;
      transition: border-color 0.15s, color 0.15s;
    }

    .btn-close:hover {
      border-color: #0C3257;
      color: #0C3257;
    }

    .btn-apply {
      flex: 1;
      height: 40px;
      border: none;
      background: #0C3257;
      border-radius: 6px;
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #ffffff;
      cursor: pointer;
      transition: background 0.15s;
    }

    .btn-apply:hover {
      background: #0e3d6b;
    }
  `]
})
export class TaskFilterComponent implements OnInit, OnDestroy {
  @Input() availableStatuses: TaskStatus[] = [];
  @Input() availableTags: TaskTag[] = [];
  @Input() availableUsers: UserDetail[] = [];
  @Input() initialFilter?: TaskFilter;

  @Output() filterChange = new EventEmitter<TaskFilter>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  localSearch: string = '';
  localStatusId: number | null = null;
  localAssigneeId: number | null = null;
  localTagId: number | null = null;
  localDateFrom: Date | null = null;
  localDateTo: Date | null = null;
  localShowInactive: boolean = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private timezoneService: TimezoneService) {}

  ngOnInit() {
    if (this.initialFilter) {
      this.localSearch = this.initialFilter.searchText || '';
      this.localStatusId = this.initialFilter.statusIds?.[0] ?? null;
      this.localAssigneeId = this.initialFilter.assignedToIds?.[0] ?? null;
      this.localTagId = this.initialFilter.tagIds?.[0] ?? null;
      this.localDateFrom = this.timezoneService.parseStoredDateToPickerDate(this.initialFilter.endDateFrom) ?? null;
      this.localDateTo = this.timezoneService.parseStoredDateToPickerDate(this.initialFilter.endDateTo) ?? null;
      this.localShowInactive = this.initialFilter.showInactiveOnly ?? false;
    }

    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {});
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInput() {
    this.searchSubject.next(this.localSearch);
  }

  onClose() {
    this.close.emit();
  }

  clearAll() {
    this.localSearch = '';
    this.localStatusId = null;
    this.localAssigneeId = null;
    this.localTagId = null;
    this.localDateFrom = null;
    this.localDateTo = null;
    this.localShowInactive = false;
    this.clearFilters.emit();
    this.filterChange.emit({});
  }

  applyFilters() {
    const filter: TaskFilter = {};
    if (this.localSearch?.trim()) { filter.searchText = this.localSearch.trim(); }
    if (this.localStatusId != null) { filter.statusIds = [this.localStatusId]; }
    if (this.localAssigneeId != null) { filter.assignedToIds = [this.localAssigneeId]; }
    if (this.localTagId != null) { filter.tagIds = [this.localTagId]; }
    if (this.localDateFrom) { filter.endDateFrom = this.timezoneService.buildDateTimestamp(this.localDateFrom); }
    if (this.localDateTo) { filter.endDateTo = this.timezoneService.buildDateTimestamp(this.localDateTo); }
    if (this.localShowInactive) { filter.showInactiveOnly = true; }
    this.filterChange.emit(filter);
    this.close.emit();
  }
}
